/* ------------------------------------------------------------------
   AccountSettingsPage.js  (v2.0 – modernized UI, matches dashboard theme)
------------------------------------------------------------------ */
import React, { useState, useEffect, useRef } from 'react'
import { TIMEZONES } from '../../../utils/timezones'
import axios from 'axios'

const inputStyle = {
  width       : '100%',
  padding     : '9px 12px',
  borderRadius: 8,
  border      : '1.5px solid #e5e7eb',
  fontSize    : 13,
  fontWeight  : 500,
  color       : '#111827',
  background  : '#ffffff',
  outline     : 'none',
  transition  : 'border-color 0.15s',
  boxSizing   : 'border-box',
}

const labelStyle = {
  fontSize   : 13,
  fontWeight : 600,
  color      : '#374151',
  marginBottom: 6,
  display    : 'block',
}

const sectionCard = {
  background  : '#ffffff',
  border      : '1px solid #e5e7eb',
  borderRadius: 12,
  padding     : '24px',
  marginBottom: 20,
  boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
}

export default function AccountSettingsPage () {
  const [userId,      setUserId]      = useState(null)
  const [name,        setName]        = useState('')
  const [email,       setEmail]       = useState('')
  const [timezone,    setTimezone]    = useState('UTC')
  const [password,    setPassword]    = useState('')
  const [avatarUrl,   setAvatarUrl]   = useState(null)
  const [showPassword,setShowPassword]= useState(false)
  const [message,     setMessage]     = useState('')
  const [error,       setError]       = useState('')
  const [avatarFile,  setAvatarFile]  = useState(null)
  const [preview,     setPreview]     = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const fileInputRef = useRef(null)

  useEffect(() => {
    axios.get('/api/auth/profile', { withCredentials: true })
      .then(res => {
        const d = res.data
        setUserId(d.id)
        setName(d.name || '')
        setEmail(d.email || '')
        setTimezone(d.timezone || 'UTC')
        setAvatarUrl(d.avatar_url)
        setPreview(d.avatar_url)
      })
      .catch(() => setError('Could not load profile. You may not be logged in.'))
  }, [])

  const handleAvatarSelect = e => {
    const file = e.target.files[0]
    if (file) { setAvatarFile(file); setPreview(URL.createObjectURL(file)) }
  }

  const handleAvatarUpload = async e => {
    e.preventDefault()
    if (!avatarFile) return
    setIsUploading(true); setMessage(''); setError('')
    const formData = new FormData()
    formData.append('userId', userId)
    formData.append('avatar', avatarFile)
    try {
      const { data } = await axios.post('/api/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      })
      setAvatarUrl(data.avatarUrl)
      setPreview(data.avatarUrl)
      setAvatarFile(null)
      setMessage('Avatar updated successfully!')
    } catch {
      setError('Avatar upload failed. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSaveDetails = e => {
    e.preventDefault(); setMessage(''); setError('')
    const body = { name, email, timezone, ...(password && { password }) }
    axios.put('/api/auth/profile', body, { withCredentials: true })
      .then(res => setMessage(res.data.message || 'Profile updated'))
      .catch(() => setError('Failed to update profile.'))
  }

  if (error && !name)
    return (
      <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'14px 18px', color:'#dc2626', fontSize:13 }}>
        {error}
      </div>
    )

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Account Settings</h2>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>Manage your profile, email, and password.</p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#dc2626', fontWeight:500 }}>
          <i className='fas fa-exclamation-circle me-2' />{error}
        </div>
      )}
      {message && (
        <div style={{ background:'#dcfce7', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#16a34a', fontWeight:500 }}>
          <i className='fas fa-check-circle me-2' />{message}
        </div>
      )}

      {/* Avatar section */}
      <div style={sectionCard}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Profile Photo</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Click the avatar to choose a new photo.</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
          >
            <img
              src={preview || 'https://placehold.co/80x80/e5e7eb/9ca3af?text=?'}
              alt='Avatar'
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e7eb' }}
            />
            <div style={{
              position      : 'absolute', bottom: 0, right: 0,
              width         : 24, height: 24, borderRadius: '50%',
              background    : '#2563eb', border: '2px solid #fff',
              display       : 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className='fas fa-camera' style={{ fontSize: 9, color: '#fff' }} />
            </div>
          </div>

          <div>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                background: 'transparent', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'block', marginBottom: 8,
              }}
            >
              Choose Photo
            </button>
            {avatarFile && (
              <button
                onClick={handleAvatarUpload}
                disabled={isUploading}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: isUploading ? '#93c5fd' : '#2563eb', color: '#fff',
                  fontSize: 13, fontWeight: 700, cursor: isUploading ? 'not-allowed' : 'pointer',
                }}
              >
                {isUploading ? 'Uploading…' : 'Save Photo'}
              </button>
            )}
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>JPG, PNG or GIF. Max 5MB.</p>
          </div>
        </div>

        <input ref={fileInputRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={handleAvatarSelect} />
      </div>

      {/* Profile details */}
      <div style={sectionCard}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Personal Information</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>Update your name, email, timezone, and password.</p>

        <form onSubmit={handleSaveDetails}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                style={inputStyle}
                value={name}
                onChange={e => setName(e.target.value)}
                required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type='email'
                style={inputStyle}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Time Zone</label>
            <select
              style={inputStyle}
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              onFocus={e => e.target.style.borderColor = '#2563eb'}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            >
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>New Password <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                style={{ ...inputStyle, paddingRight: 80 }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Leave blank to keep current'
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: '#6b7280',
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type='submit'
            style={{
              padding: '10px 22px', borderRadius: 9, border: 'none',
              background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
            onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
          >
            Save Changes
          </button>
        </form>
      </div>
    </div>
  )
}